import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Typography, Avatar, Dropdown, Spin, Tooltip, Button } from 'antd';
import {
  DashboardOutlined,
  BookOutlined,
  UserOutlined,
  LogoutOutlined,
  TeamOutlined,
  FileAddOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  FunctionOutlined,
  ExperimentOutlined,
  ScissorOutlined,
  CheckSquareOutlined,
  FileTextOutlined,
  ToolOutlined,
  FileSearchOutlined,
  FlagOutlined,
  ReadOutlined,
  CreditCardOutlined,
  ShareAltOutlined,
  FunnelPlotOutlined,
  WalletOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { AuthProvider, useAuth } from './shared';
import AdminLogin from './pages/AdminLogin';
import { ErrorBoundary } from './components/ErrorBoundary';

// Lazy load pages for better performance
const Dashboard = lazy(() => import('./pages/Dashboard'));
const QuestionCreator = lazy(() => import('./pages/QuestionCreatorNew'));
const QuestionList = lazy(() => import('./pages/QuestionList'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const PDFQuestionGenerator = lazy(() => import('./pages/PDFQuestionGenerator'));
const PDFQuestionEditorEnhanced = lazy(() => import('./pages/PDFQuestionEditorEnhanced'));
const PDFQuestionList = lazy(() => import('./pages/PDFQuestionList'));
const LatexViewer = lazy(() => import('./pages/LatexViewer'));
const GeometryLabNew = lazy(() => import('./pages/GeometryLabNew'));
const SmartCrop = lazy(() => import('./pages/SmartCrop'));
const TestReviewer = lazy(() => import('./pages/TestReviewer'));
const TestManagement = lazy(() => import('./pages/TestManagement'));
const ClassManagement = lazy(() => import('./pages/ClassManagement'));
const TestAttempts = lazy(() => import('./pages/TestAttempts'));
const WritingReview = lazy(() => import('./pages/WritingReview'));
const DevPdfParser = lazy(() => import('./pages/DevPdfParser'));
const DevPdfParseList = lazy(() => import('./pages/DevPdfParseList'));
const Profile = lazy(() => import('./pages/Profile'));
const QuestionReports = lazy(() => import('./pages/QuestionReports'));
const ReadingSetBuilder = lazy(() => import('./pages/ReadingSetBuilder'));
const ClozeBuilder = lazy(() => import('./pages/ClozeBuilder'));
const Billing = lazy(() => import('./pages/Billing'));
const PartnerRegister = lazy(() => import('./pages/PartnerRegister'));
const Partners = lazy(() => import('./pages/Partners'));
const PartnerDashboard = lazy(() => import('./pages/PartnerDashboard'));
const GtmFunnel = lazy(() => import('./pages/GtmFunnel'));
const Payouts = lazy(() => import('./pages/Payouts'));
const FacebookMonitor = lazy(() => import('./pages/FacebookMonitor'));

const { Header, Sider, Content } = Layout;
const { Title } = Typography;

const AdminDashboard: React.FC = () => {
  const [collapsed, setCollapsed] = React.useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
    },
  ];

  // 根据当前路径确定选中的菜单项
  const getCurrentSelectedKey = () => {
    const pathname = location.pathname;
    if (pathname === '/') return '/';
    if (pathname === '/user-management') return '/user-management';
    if (pathname === '/questions') return '/questions';
    if (pathname === '/question-creator') return '/question-creator';
    if (pathname === '/question-lab') return '/question-lab';
    if (pathname.startsWith('/question-lab/edit')) return '/question-lab';
    if (pathname.startsWith('/reading-set-builder')) return '/reading-set-builder';
    if (pathname.startsWith('/cloze-builder')) return '/cloze-builder';
    if (pathname === '/latex-viewer') return '/latex-viewer';
    if (pathname === '/geometry-lab') return '/geometry-lab';
    if (pathname === '/smart-crop') return '/smart-crop';
    if (pathname === '/test-reviewer') return '/test-reviewer';
    if (pathname === '/test-management') return '/test-management';
    if (pathname === '/class-management') return '/class-management';
    if (pathname === '/test-attempts') return '/test-attempts';
    if (pathname === '/question-reports') return '/question-reports';
    if (pathname === '/billing') return '/billing';
    if (pathname === '/gtm-funnel') return '/gtm-funnel';
    if (pathname === '/partners') return '/partners';
    if (pathname === '/payouts') return '/payouts';
    if (pathname === '/facebook-monitor') return '/facebook-monitor';
    if (pathname.startsWith('/dev/')) return '/dev/pdf-parse-list';
    return '/';
  };

  const getDefaultOpenKeys = () => {
    const pathname = location.pathname;
    if (['/latex-viewer', '/geometry-lab', '/smart-crop'].includes(pathname)) return ['authoring'];
    return [];
  };

  // Sidebar nav: a flat list of 14 items was hard to scan, so pages are
  // grouped by workflow with `type: 'group'` section headers. The three
  // rarely-used authoring utilities live under one collapsible submenu.
  const navIcon = (title: string, node: React.ReactNode) =>
    collapsed ? <Tooltip placement="right" title={title}>{node}</Tooltip> : node;

  const menuItems = [
    {
      key: '/',
      icon: navIcon('Dashboard', <DashboardOutlined />),
      label: 'Dashboard',
    },
    {
      type: 'group' as const,
      label: 'Content',
      children: [
        {
          key: '/questions',
          icon: navIcon('Question Bank', <BookOutlined />),
          label: 'Question Bank',
        },
        {
          key: '/question-lab',
          icon: navIcon('Question Lab', <FileAddOutlined />),
          label: 'Question Lab',
        },
        {
          key: '/reading-set-builder',
          icon: navIcon('Reading Sets', <ReadOutlined />),
          label: 'Reading Sets',
        },
        {
          key: '/cloze-builder',
          icon: navIcon('Cloze Builder', <FileTextOutlined />),
          label: 'Cloze Builder',
        },
        {
          key: 'authoring',
          icon: navIcon('Authoring Tools', <ToolOutlined />),
          label: 'Authoring Tools',
          children: [
            {
              key: '/latex-viewer',
              icon: <FunctionOutlined />,
              label: 'LaTeX Viewer',
            },
            {
              key: '/geometry-lab',
              icon: <ExperimentOutlined />,
              label: 'Geometry Lab',
            },
            {
              key: '/smart-crop',
              icon: <ScissorOutlined />,
              label: 'Smart Crop',
            },
          ],
        },
      ],
    },
    {
      type: 'group' as const,
      label: 'Assessments',
      children: [
        {
          key: '/test-management',
          icon: navIcon('Test Management', <FileTextOutlined />),
          label: 'Test Management',
        },
        {
          key: '/test-reviewer',
          icon: navIcon('Test Reviewer', <CheckSquareOutlined />),
          label: 'Test Reviewer',
        },
        {
          key: '/test-attempts',
          icon: navIcon('Test Attempts', <FileTextOutlined />),
          label: 'Test Attempts',
        },
        {
          key: '/writing-review',
          icon: navIcon('Writing Review', <CheckSquareOutlined />),
          label: 'Writing Review',
        },
      ],
    },
    {
      type: 'group' as const,
      label: 'People',
      children: [
        {
          key: '/user-management',
          icon: navIcon('User Management', <TeamOutlined />),
          label: 'User Management',
        },
        {
          key: '/class-management',
          icon: navIcon('Class Management', <BookOutlined />),
          label: 'Class Management',
        },
        {
          key: '/partners',
          icon: navIcon('Partners', <ShareAltOutlined />),
          label: 'Partners',
        },
      ],
    },
    {
      type: 'group' as const,
      label: 'Moderation',
      children: [
        {
          key: '/question-reports',
          icon: navIcon('Question Reports', <FlagOutlined />),
          label: 'Question Reports',
        },
      ],
    },
    {
      type: 'group' as const,
      label: 'Business',
      children: [
        {
          key: '/billing',
          icon: navIcon('Billing', <CreditCardOutlined />),
          label: 'Billing',
        },
        {
          key: '/payouts',
          icon: navIcon('Payouts', <WalletOutlined />),
          label: 'Payouts',
        },
        {
          key: '/gtm-funnel',
          icon: navIcon('GTM Funnel', <FunnelPlotOutlined />),
          label: 'GTM Funnel',
        },
      ],
    },
    {
      type: 'group' as const,
      label: 'Intelligence',
      children: [
        {
          key: '/facebook-monitor',
          icon: navIcon('FB Monitor', <GlobalOutlined />),
          label: 'FB Monitor',
        },
      ],
    },
    {
      type: 'group' as const,
      label: 'Developer',
      children: [
        {
          key: '/dev/pdf-parse-list',
          icon: navIcon('PDF Parser', <FileSearchOutlined />),
          label: 'PDF Parser',
        },
      ],
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        theme="light"
        width={256}
        collapsedWidth={80}
        trigger={null}
      >
        <div style={{ 
          padding: collapsed ? '16px 8px' : '16px', 
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {collapsed ? (
            <Tooltip placement="right" title="Cleveroo Admin">
              <div style={{ 
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '16px'
              }}>
                CA
              </div>
            </Tooltip>
          ) : (
            <Title level={4} style={{ margin: 0, textAlign: 'center' }}>
              Cleveroo Admin
            </Title>
          )}
        </div>
        <Menu
          mode="inline"
          selectedKeys={[getCurrentSelectedKey()]}
          defaultOpenKeys={getDefaultOpenKeys()}
          style={{ height: '100%', borderRight: 0 }}
          items={menuItems}
          onClick={({ key }: { key: string }) => {
            navigate(key);
          }}
        />
      </Sider>
      <Layout>
        <Header style={{ padding: 0, background: '#fff', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ padding: '0 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                style={{
                  fontSize: '16px',
                  width: 40,
                  height: 40,
                }}
              />
              <Title level={3} style={{ margin: 0, lineHeight: '64px' }}>
                Question Management System
              </Title>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <span>Welcome, {user?.name}</span>
              <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
                <Avatar 
                  src={user?.avatar} 
                  icon={<UserOutlined />} 
                  style={{ cursor: 'pointer' }}
                />
              </Dropdown>
            </div>
          </div>
        </Header>
        <Content style={{ margin: 0, overflow: 'initial' }}>
          <div className="content-container">
            <ErrorBoundary><Suspense fallback={
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '200px' 
              }}>
                <Spin size="large" />
              </div>
            }>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/user-management" element={<UserManagement />} />
                <Route path="/questions" element={<QuestionList />} />
                <Route path="/question-creator" element={<QuestionCreator />} />
                <Route path="/question-creator/edit/:id" element={<QuestionCreator />} />
                <Route path="/question-bank" element={<QuestionList />} />
                <Route path="/question-lab" element={<PDFQuestionList />} />
                <Route path="/question-lab/upload" element={<PDFQuestionGenerator />} />
                <Route path="/question-lab/edit/:uploadId" element={<PDFQuestionEditorEnhanced />} />
                <Route path="/reading-set-builder" element={<ReadingSetBuilder />} />
                <Route path="/reading-set-builder/edit/:passageId" element={<ReadingSetBuilder />} />
                <Route path="/cloze-builder" element={<ClozeBuilder />} />
                <Route path="/latex-viewer" element={<LatexViewer />} />
                <Route path="/geometry-lab" element={<GeometryLabNew />} />
                <Route path="/smart-crop" element={<SmartCrop />} />
                <Route path="/test-reviewer" element={<TestReviewer />} />
                <Route path="/test-management" element={<TestManagement />} />
                <Route path="/class-management" element={<ClassManagement />} />
                <Route path="/test-attempts" element={<TestAttempts />} />
                <Route path="/writing-review" element={<WritingReview />} />
                <Route path="/question-reports" element={<QuestionReports />} />
                <Route path="/billing" element={<Billing />} />
                <Route path="/gtm-funnel" element={<GtmFunnel />} />
                <Route path="/partners" element={<Partners />} />
                <Route path="/payouts" element={<Payouts />} />
                <Route path="/facebook-monitor" element={<FacebookMonitor />} />
                <Route path="/dev/pdf-parser" element={<DevPdfParser />} />
                <Route path="/dev/pdf-parser/:id" element={<DevPdfParser />} />
                <Route path="/dev/pdf-parse-list" element={<DevPdfParseList />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense></ErrorBoundary>
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

// Public routes that bypass auth entirely — matched by pathname prefix
const PUBLIC_PATHS = ['/partner/register'];

function App() {
  const location = useLocation();
  const isPublic = PUBLIC_PATHS.some((p) => location.pathname.startsWith(p));

  if (isPublic) {
    return (
      <ErrorBoundary><Suspense fallback={
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <Spin size="large" />
        </div>
      }>
        <Routes>
          <Route path="/partner/register" element={<PartnerRegister />} />
        </Routes>
      </Suspense></ErrorBoundary>
    );
  }

  return (
    <AuthProvider storagePrefix="cleveroo_admin">
      <AuthGuard />
    </AuthProvider>
  );
}

// PartnerShell: minimal layout for partner-role users — just the dashboard + no admin nav.
const PartnerShell: React.FC = () => (
  <ErrorBoundary><Suspense fallback={
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <Spin size="large" />
    </div>
  }>
    <Routes>
      <Route path="/partner" element={<PartnerDashboard />} />
      <Route path="*" element={<Navigate to="/partner" replace />} />
    </Routes>
  </Suspense></ErrorBoundary>
);

const AuthGuard: React.FC = () => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminLogin />;
  }

  // Partner users get their own portal shell — they cannot see any admin pages.
  if (user?.role === 'partner') {
    return <PartnerShell />;
  }

  return <AdminDashboard />;
};

export default App; 